import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Segmented, Button, Modal, Select, InputNumber, Input, Tooltip, Spin, Space,
  notification, Popconfirm, Empty,
} from 'antd';
import {
  PlusOutlined, ImportOutlined, CopyOutlined, InfoCircleOutlined, EditOutlined, DeleteOutlined,
} from '@ant-design/icons';
import pmApi from './pmApi';
import SetupSection from './SetupSection';

/**
 * Concept A — the schedule grid, plan-scoped (testing findings #1/#3):
 * the grid shows ONLY benefits added to THIS plan (its coverage rows).
 * The benefit catalog stays global — that is what makes facility mappings
 * reusable — but catalog items never appear on a plan until explicitly
 * added here or brought in by copy/import. Benefits not added are excluded
 * by default (default-deny).
 */

const fmtMoney = (v) => `₦${Number(v || 0).toLocaleString('en-NG', { maximumFractionDigits: 0 })}`;
const METRIC_UNITS_SINGULAR = { money: '₦', count: 'time', days: 'day', quantity: 'unit' };
const METRIC_UNITS_PLURAL = { money: '₦', count: 'times', days: 'days', quantity: 'units' };
const METRIC_OPTIONS = [
  { value: 'money', label: 'Money (₦)' },
  { value: 'count', label: 'Count' },
  { value: 'days', label: 'Days' },
  { value: 'quantity', label: 'Quantity' },
  { value: 'text', label: 'Text (descriptive)' },
];

const idList = (csv) => String(csv || '').split(',').map((x) => parseInt(x, 10)).filter(Boolean);

const windowChip = (pool) => {
  if (!pool) return null;
  if (pool.window === 'calendar_month') return 'monthly';
  if (pool.window === 'rolling_months') return `rolling ${pool.window_months} mo`;
  return 'policy year';
};

// Pluralises count/day/quantity units ("1 day" vs "2 days"); money has no unit word.
const pluralizeUnit = (value, metric) => {
  const n = Number(value);
  const units = n === 1 ? METRIC_UNITS_SINGULAR : METRIC_UNITS_PLURAL;
  return units[metric] || '';
};

const limitLabel = (pool) => {
  if (!pool) return '';
  if (pool.metric === 'text') return pool.limit_text || 'unlimited';
  if (pool.limit_value === null || pool.limit_value === undefined) return 'unlimited';
  return pool.metric === 'money'
    ? fmtMoney(pool.limit_value)
    : `${Number(pool.limit_value)} ${pluralizeUnit(pool.limit_value, pool.metric)}`;
};

function CapEditorModal({ open, onClose, planId, row, pools, onSaved }) {
  const ownPool = row?.pool && Number(row.pool.subscriber_count || 1) <= 1 ? row.pool : null;
  const [mode, setMode] = useState('own');
  const [metric, setMetric] = useState('money');
  const [limit, setLimit] = useState(null);
  const [limitText, setLimitText] = useState('');
  const [window_, setWindow] = useState('policy_year');
  const [months, setMonths] = useState(24);
  const [sharedPool, setSharedPool] = useState(null);
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setMode(row?.pool && Number(row.pool.subscriber_count) > 1 ? 'shared' : 'own');
    setMetric(row?.pool?.metric || 'money');
    setLimit(row?.pool?.limit_value !== null && row?.pool ? Number(row.pool.limit_value) : null);
    setLimitText(row?.pool?.limit_text || '');
    setWindow(row?.pool?.window || 'policy_year');
    setMonths(row?.pool?.window_months || 24);
    setSharedPool(row?.pool && Number(row.pool.subscriber_count) > 1 ? row.pool.id : null);
    setNote(row?.coverage?.adjudication_note || '');
  }, [open, row]);

  const sharablePools = useMemo(
    () => (pools || []).filter((p) => p.kind === 'benefit' && (!ownPool || p.id !== ownPool.id)),
    [pools, ownPool]
  );

  const save = async () => {
    setSaving(true);
    try {
      let poolId;
      if (mode === 'shared') {
        if (!sharedPool) { notification.warning({ message: 'Pick the shared pool' }); return; }
        poolId = sharedPool;
      } else {
        if (metric === 'text') {
          if (!limitText.trim()) { notification.warning({ message: 'Describe the limit' }); return; }
        } else if (!limit || limit <= 0) {
          notification.warning({ message: 'Enter a positive limit' }); return;
        }
        const body = {
          name: row.item.name,
          kind: 'benefit',
          metric,
          limit_value: metric === 'text' ? null : limit,
          limit_text: metric === 'text' ? limitText.trim() : null,
          window: window_,
          window_months: window_ === 'rolling_months' ? months : undefined,
        };
        if (ownPool) {
          const res = await pmApi.updatePool(planId, ownPool.id, body);
          if (!res.ok) return;
          poolId = ownPool.id;
        } else {
          const res = await pmApi.createPool(planId, body);
          if (!res.ok) return;
          poolId = res.data.id;
        }
      }
      const cov = await pmApi.setCoverage(planId, row.item.id, {
        state: 'capped',
        pool_id: poolId,
        adjudication_note: note || null,
      });
      if (cov.ok) {
        onSaved();
        onClose();
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      open={open}
      onCancel={onClose}
      onOk={save}
      okText="Save cap"
      confirmLoading={saving}
      title={<span>Cap — {row?.item?.name}</span>}
      destroyOnClose
    >
      <Space direction="vertical" style={{ width: '100%' }} size={12}>
        <Segmented
          value={mode}
          onChange={setMode}
          options={[
            { label: 'Own pool', value: 'own' },
            { label: 'Shared pool', value: 'shared' },
          ]}
        />
        {mode === 'own' ? (
          <Space wrap>
            <Select
              value={metric}
              onChange={setMetric}
              style={{ width: 160 }}
              options={METRIC_OPTIONS}
            />
            {metric === 'text' ? (
              <Input
                value={limitText}
                onChange={(e) => setLimitText(e.target.value)}
                style={{ width: 220 }}
                placeholder="e.g. General ward only"
              />
            ) : (
              <InputNumber
                value={limit}
                onChange={setLimit}
                min={1}
                style={{ width: 160 }}
                placeholder="Limit"
                formatter={(v) => (metric === 'money' ? `${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',') : `${v}`)}
                parser={(v) => `${v}`.replace(/,/g, '')}
              />
            )}
            <Select
              value={window_}
              onChange={setWindow}
              style={{ width: 160 }}
              options={[
                { value: 'policy_year', label: 'Policy year' },
                { value: 'calendar_month', label: 'Calendar month' },
                { value: 'rolling_months', label: 'Rolling months' },
              ]}
            />
            {window_ === 'rolling_months' ? (
              <InputNumber value={months} onChange={setMonths} min={1} max={120} addonAfter="mo" style={{ width: 110 }} />
            ) : null}
          </Space>
        ) : (
          <Select
            value={sharedPool}
            onChange={setSharedPool}
            style={{ width: '100%' }}
            placeholder="Subscribe to an existing pool (e.g. Accommodation)"
            options={sharablePools.map((p) => ({
              value: p.id,
              label: `${p.name} — ${limitLabel(p)} · ${windowChip(p)}`,
            }))}
            notFoundContent="No other benefit pools on this plan yet"
          />
        )}
        <Input.TextArea
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder="Adjudication note (surfaced to CO at PA — never machine-evaluated)"
          autoSize={{ minRows: 2, maxRows: 4 }}
        />
      </Space>
    </Modal>
  );
}

/**
 * Catalog item editor: categories (first = primary), check-in types — catalog-level,
 * applies everywhere this item is used. Renaming is deliberately NOT available here:
 * a benefit item's name is only editable on the Benefit Items (mapping) page, so it
 * can't drift silently while someone is just designing a plan.
 */
function ItemEditorModal({ open, onClose, meta, item, onSaved }) {
  const [categories, setCategories] = useState([]);
  const [types, setTypes] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setCategories(item?.costable_category_ids?.length ? item.costable_category_ids : (item?.primaryCat ? [item.primaryCat] : []));
    setTypes(item?.checkin_type_ids || []);
  }, [open, item]);

  const save = async () => {
    if (!categories.length) {
      notification.warning({ message: 'At least one request category is required' });
      return;
    }
    setSaving(true);
    const res = await pmApi.updateBenefitItem(item.id, {
      costable_category_ids: categories,
      checkin_type_ids: types,
    });
    setSaving(false);
    if (res.ok) { onSaved(); onClose(); }
  };

  return (
    <Modal
      open={open}
      onCancel={onClose}
      onOk={save}
      okText="Save"
      confirmLoading={saving}
      title={<span>Edit benefit item — {item?.name}</span>}
      destroyOnClose
    >
      <Space direction="vertical" style={{ width: '100%' }} size={10}>
        <Input value={item?.name || ''} disabled />
        <Select
          mode="multiple"
          style={{ width: '100%' }}
          placeholder="Request categories (first is the primary)"
          value={categories}
          onChange={setCategories}
          options={(meta?.categories || []).map((c) => ({ value: c.id, label: c.category_name }))}
        />
        <Select
          mode="multiple"
          style={{ width: '100%' }}
          placeholder="Applicable check-in types"
          value={types}
          onChange={setTypes}
          options={(meta?.checkin_types || []).map((t) => ({ value: t.checkin_type_id, label: t.checkin_type_name }))}
        />
        <span className="pm-benefit-sub">
          Catalog-level edit: applies everywhere this item is used (it is one global catalog —
          that is what makes facility mappings reusable). To rename this item, use the Benefit
          Items page.
        </span>
      </Space>
    </Modal>
  );
}

export default function DesignerTab({ planId, plan, plans, meta, onDesignChanged, catalogVersion = 0 }) {
  const [design, setDesign] = useState(null);
  const [catalog, setCatalog] = useState([]);
  const [loading, setLoading] = useState(false);
  const [capRow, setCapRow] = useState(null);
  const [editItem, setEditItem] = useState(null);
  const [noteRow, setNoteRow] = useState(null);
  const [noteDraft, setNoteDraft] = useState('');
  const [addOpen, setAddOpen] = useState(false);
  const [addPick, setAddPick] = useState(null);
  const [addState, setAddState] = useState('uncapped');
  const [addPoolMode, setAddPoolMode] = useState('own');
  const [addMetric, setAddMetric] = useState('money');
  const [addLimit, setAddLimit] = useState(null);
  const [addLimitText, setAddLimitText] = useState('');
  const [addWindow, setAddWindow] = useState('policy_year');
  const [addMonths, setAddMonths] = useState(24);
  const [addSharedPool, setAddSharedPool] = useState(null);
  const [addNote, setAddNote] = useState('');
  const [copyOpen, setCopyOpen] = useState(false);
  const [copySource, setCopySource] = useState(null);
  const [dismissedWarnings, setDismissedWarnings] = useState({});
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [detailsName, setDetailsName] = useState('');
  const [detailsSegments, setDetailsSegments] = useState([]);
  const [detailsSaving, setDetailsSaving] = useState(false);

  const currentPlan = useMemo(
    () => (plans || []).find((p) => Number(p.plan_id) === Number(planId)) || null,
    [plans, planId]
  );

  const openDetails = () => {
    setDetailsName(currentPlan?.plan_name || '');
    setDetailsSegments(String(currentPlan?.plan_segments || '').split(',').map((s) => s.trim()).filter(Boolean));
    setDetailsOpen(true);
  };

  const saveDetails = async () => {
    if (!detailsName.trim()) { notification.warning({ message: 'Name is required' }); return; }
    const isUtility = design?.profile?.plan_type === 'utility';
    if (!isUtility && !detailsSegments.length) {
      notification.warning({ message: 'Pick at least one segment' }); return;
    }
    setDetailsSaving(true);
    const body = { plan_name: detailsName.trim() };
    if (!isUtility) body.plan_segments = detailsSegments;
    const res = await pmApi.updatePlanDetails(planId, body);
    setDetailsSaving(false);
    if (res.ok) {
      notification.success({ message: 'Plan details updated' });
      setDetailsOpen(false);
      onDesignChanged?.();
    }
  };

  const typeName = useCallback(
    (id) => (meta?.checkin_types || []).find((t) => Number(t.checkin_type_id) === Number(id))?.checkin_type_name || `type ${id}`,
    [meta]
  );
  const categoryName = useCallback(
    (id) => (meta?.categories || []).find((c) => Number(c.id) === Number(id))?.category_name || `category ${id}`,
    [meta]
  );

  // The catalog is global and edited on the Benefit Item tab, which stays mounted
  // beside this one. Re-read it whenever that tab reports a change and every time
  // the picker opens, so a Benefit Item created a moment ago is selectable at once.
  const loadCatalog = useCallback(async () => {
    const c = await pmApi.benefitItems();
    if (c.ok) setCatalog(c.data.benefit_items || []);
  }, []);

  const load = useCallback(async () => {
    if (!planId) return;
    setLoading(true);
    const [d] = await Promise.all([pmApi.design(planId), loadCatalog()]);
    if (d.ok) setDesign(d.data);
    setLoading(false);
  }, [planId, loadCatalog]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (catalogVersion > 0) loadCatalog(); }, [catalogVersion, loadCatalog]);
  useEffect(() => {
    if (!addOpen) return;
    loadCatalog();
    setAddPick(null);
    setAddState('uncapped');
    setAddPoolMode('own');
    setAddMetric('money');
    setAddLimit(null);
    setAddLimitText('');
    setAddWindow('policy_year');
    setAddMonths(24);
    setAddSharedPool(null);
    setAddNote('');
  }, [addOpen, loadCatalog]);

  const addSharablePools = useMemo(() => (design?.pools || []).filter((p) => p.kind === 'benefit'), [design]);

  const poolsById = useMemo(() => {
    const m = {};
    (design?.pools || []).forEach((p) => { m[Number(p.id)] = p; });
    return m;
  }, [design]);

  // Plan-scoped rows: ONLY this plan's coverage rows (findings #1/#3).
  const rows = useMemo(() => {
    const grouped = {};
    (design?.coverage || []).forEach((cov) => {
      const item = {
        id: Number(cov.benefit_item_id),
        name: cov.benefit_item_name,
        primaryCat: Number(cov.costable_category_id),
        costable_category_ids: idList(cov.costable_category_ids),
        checkin_type_ids: idList(cov.checkin_type_ids),
      };
      const pool = cov.pool_id ? poolsById[Number(cov.pool_id)] : null;
      const key = item.primaryCat;
      if (!grouped[key]) grouped[key] = [];
      grouped[key].push({ item, coverage: cov, pool });
    });
    return Object.entries(grouped)
      .map(([catId, items]) => ({ catId: Number(catId), items }))
      .sort((a, b) => categoryName(a.catId).localeCompare(categoryName(b.catId)));
  }, [design, poolsById, categoryName]);

  const onPlanItemIds = useMemo(
    () => new Set((design?.coverage || []).map((c) => Number(c.benefit_item_id))),
    [design]
  );

  const envelopes = useMemo(() => (design?.pools || []).filter((p) => p.kind === 'envelope'), [design]);

  const setState = async (row, state) => {
    if (state === 'capped') { setCapRow(row); return; }
    const res = await pmApi.setCoverage(planId, row.item.id, {
      state,
      adjudication_note: row.coverage?.adjudication_note || null,
    });
    if (res.ok) { load(); onDesignChanged?.(); }
  };

  const clearRow = async (row) => {
    const res = await pmApi.removeCoverage(planId, row.item.id);
    if (res.ok) { load(); onDesignChanged?.(); }
  };

  const saveNote = async () => {
    const row = noteRow;
    const state = row.coverage?.state || 'excluded';
    const body = { state, adjudication_note: noteDraft || null };
    if (state === 'capped') body.pool_id = row.coverage.pool_id;
    const res = await pmApi.setCoverage(planId, row.item.id, body);
    if (res.ok) { setNoteRow(null); load(); }
  };

  const saveEnvelope = async (pool, value) => {
    const res = await pmApi.updatePool(planId, pool.id, {
      name: pool.name, kind: 'envelope', checkin_type_id: pool.checkin_type_id,
      metric: pool.metric, limit_value: value || null, window: pool.window,
      window_months: pool.window_months,
    });
    if (res.ok) load();
  };

  const addEnvelope = async (checkinTypeId) => {
    const res = await pmApi.createPool(planId, {
      name: `${typeName(checkinTypeId)} envelope`,
      kind: 'envelope',
      checkin_type_id: checkinTypeId,
      metric: 'money',
      limit_value: null,
    });
    if (res.ok) load();
  };

  const addExisting = async () => {
    if (!addPick) { notification.warning({ message: 'Pick a benefit from the catalog' }); return; }

    let poolId = null;
    if (addState === 'capped') {
      if (addPoolMode === 'shared') {
        if (!addSharedPool) { notification.warning({ message: 'Pick the shared pool' }); return; }
        poolId = addSharedPool;
      } else {
        if (addMetric === 'text') {
          if (!addLimitText.trim()) { notification.warning({ message: 'Describe the limit' }); return; }
        } else if (!addLimit || addLimit <= 0) {
          notification.warning({ message: 'Enter a positive limit' }); return;
        }
        const picked = (catalog || []).find((i) => Number(i.id) === Number(addPick));
        const poolRes = await pmApi.createPool(planId, {
          name: picked?.name || 'Benefit pool',
          kind: 'benefit',
          metric: addMetric,
          limit_value: addMetric === 'text' ? null : addLimit,
          limit_text: addMetric === 'text' ? addLimitText.trim() : null,
          window: addWindow,
          window_months: addWindow === 'rolling_months' ? addMonths : undefined,
        });
        if (!poolRes.ok) return;
        poolId = poolRes.data.id;
      }
    }

    const body = { state: addState, adjudication_note: addNote || null };
    if (addState === 'capped') body.pool_id = poolId;
    const res = await pmApi.setCoverage(planId, addPick, body);
    if (res.ok) {
      notification.success({ message: 'Benefit added to this plan.' });
      setAddOpen(false); setAddPick(null);
      load(); onDesignChanged?.();
    }
  };

  const runClear = () => {
    Modal.confirm({
      title: 'Clear this plan’s entire design?',
      content: 'Retires every coverage row and pool in one act. Bands, prices, pricing events and the switchboard are untouched. Audited.',
      okText: 'Clear design',
      okButtonProps: { danger: true },
      onOk: async () => {
        const res = await pmApi.clearDesign(planId);
        if (res.ok) {
          notification.success({ message: 'Design cleared', description: res.message });
          load(); onDesignChanged?.();
        }
      },
    });
  };

  const runImport = (replace) => pmApi.importLegacy(planId, replace).then((res) => {
    if (res.ok) {
      notification.success({
        message: 'Imported from legacy limits',
        description: `${res.data.envelopes} envelope(s), ${res.data.benefit_pools} benefit pool(s); ${res.data.skipped.length} skipped for manual work.`,
      });
      load(); onDesignChanged?.();
    } else if ((res.message || '').includes('replace=true')) {
      Modal.confirm({
        title: 'Plan already has a design',
        content: 'Replace it with the legacy-limits import? The existing design will be cleared first — merging is not supported.',
        okText: 'Clear and import',
        okButtonProps: { danger: true },
        onOk: () => runImport(true),
      });
    }
  });

  const runCopy = (replace) => pmApi.copyDesign(planId, copySource, replace).then((res) => {
    if (res.ok) {
      notification.success({ message: 'Design copied', description: `${res.data.coverage_rows} benefits brought over without limits. ${res.data.note}` });
      setCopyOpen(false); load(); onDesignChanged?.();
    } else if ((res.message || '').includes('replace=true')) {
      Modal.confirm({
        title: 'This plan already has a design',
        content: 'Copying requires a cleared target — clear it and copy? Merging is not supported.',
        okText: 'Clear and copy',
        okButtonProps: { danger: true },
        onOk: () => runCopy(true),
      });
    }
  });

  if (!planId) return <Empty description="Pick a plan to open its design" style={{ padding: 40 }} />;
  if (loading && !design) return <div style={{ padding: 48, textAlign: 'center' }}><Spin /></div>;

  const card = design?.cardinality || { pairs: [], ambiguous: 0, auto: 0, not_covered: 0 };
  // Dependent check-in types (e.g. Annual Medicals tied to Outpatient) inherit
  // their parent's envelope — they never need (or get offered) one of their own.
  const missingEnvelopeTypes = (meta?.checkin_types || []).filter(
    (t) => !t.parent_checkin_type_id
      && !envelopes.some((e) => Number(e.checkin_type_id) === Number(t.checkin_type_id))
  );
  const dependentTypes = (meta?.checkin_types || []).filter((t) => t.parent_checkin_type_id);
  const emptyDesign = !envelopes.length && !rows.length;

  return (
    <div className="pm-designer-layout" style={{ display: 'flex', gap: 16, alignItems: 'flex-start' }}>
      <div style={{ flex: 1, minWidth: 0 }}>
        <Space style={{ marginBottom: 10 }} wrap>
          <Button type="primary" icon={<PlusOutlined />} onClick={() => setAddOpen(true)}>Add benefit…</Button>
          <Button icon={<EditOutlined />} onClick={openDetails}>Rename / segment…</Button>
          <Button icon={<ImportOutlined />} onClick={() => runImport(false)}>Import from legacy limits</Button>
          <Button icon={<CopyOutlined />} onClick={() => setCopyOpen(true)}>Copy design from…</Button>
          <Button icon={<DeleteOutlined />} danger onClick={runClear}>Clear design</Button>
        </Space>

        {emptyDesign ? (
          <Empty
            style={{ padding: 40, background: '#fff', border: '1px solid var(--pm-line)', borderRadius: 10 }}
            description={
              <span>
                Empty slate — this plan covers nothing yet (everything is excluded by default).<br />
                Add benefits, set envelopes, or copy another plan&apos;s design.
              </span>
            }
          />
        ) : (
        <div className="pm-grid" style={{ maxHeight: '68vh', overflow: 'auto' }}>
          <table>
            <thead>
              <tr>
                <th style={{ minWidth: 220 }}>Benefit</th>
                <th>Coverage</th>
                <th style={{ minWidth: 180 }}>Limit · window</th>
                <th>Applies in</th>
                <th style={{ width: 80 }} />
              </tr>
            </thead>
            <tbody>
              {envelopes.map((pool) => (
                <tr className="pm-envelope-row" key={`env-${pool.id}`}>
                  <td>{pool.name}</td>
                  <td><span className="pm-chip pm-chip-accent">check-in pool</span></td>
                  <td>
                    <InputNumber
                      size="small"
                      style={{ width: 140 }}
                      value={pool.limit_value !== null ? Number(pool.limit_value) : null}
                      placeholder="unlimited"
                      min={0}
                      formatter={(v) => (v === null || v === undefined || v === '' ? '' : `${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, ','))}
                      parser={(v) => `${v}`.replace(/,/g, '')}
                      onBlur={(e) => {
                        const raw = `${e.target.value}`.replace(/,/g, '');
                        const val = raw === '' ? null : Number(raw);
                        const current = pool.limit_value !== null ? Number(pool.limit_value) : null;
                        if (val !== current) saveEnvelope(pool, val);
                      }}
                    />
                    <span className="pm-chip pm-chip-muted" style={{ marginLeft: 8 }}>policy year</span>
                  </td>
                  <td>
                    {pool.checkin_type_id
                      ? <span className="pm-chip pm-chip-accent">{typeName(pool.checkin_type_id)}</span>
                      : <span className="pm-chip pm-chip-muted">plan-wide</span>}
                  </td>
                  <td>
                    <Popconfirm title="Remove this envelope pool?" onConfirm={async () => { const r = await pmApi.retirePool(planId, pool.id); if (r.ok) load(); }}>
                      <Button size="small" type="text" danger>×</Button>
                    </Popconfirm>
                  </td>
                </tr>
              ))}
              {missingEnvelopeTypes.length ? (
                <tr className="pm-envelope-row">
                  <td colSpan={5} style={{ fontWeight: 400 }}>
                    <Space size={6} wrap>
                      <span className="pm-benefit-sub">Add envelope:</span>
                      {missingEnvelopeTypes.map((t) => (
                        <Button key={t.checkin_type_id} size="small" onClick={() => addEnvelope(t.checkin_type_id)}>
                          {t.checkin_type_name}
                        </Button>
                      ))}
                    </Space>
                  </td>
                </tr>
              ) : null}
              {dependentTypes.length ? (
                <tr className="pm-envelope-row">
                  <td colSpan={5} style={{ fontWeight: 400 }}>
                    <span className="pm-benefit-sub">
                      {dependentTypes.map((t) => `${t.checkin_type_name} inherits ${typeName(t.parent_checkin_type_id)}'s envelope`).join(' · ')}
                    </span>
                  </td>
                </tr>
              ) : null}

              {rows.map((group) => (
                <React.Fragment key={group.catId}>
                  <tr>
                    <td colSpan={5} style={{ background: 'var(--pm-surface-2)', padding: '5px 12px', fontSize: 11, fontWeight: 600, letterSpacing: '0.06em', textTransform: 'uppercase', color: 'var(--pm-ink-faint)' }}>
                      {categoryName(group.catId)}
                    </td>
                  </tr>
                  {group.items.map((row) => {
                    const state = row.coverage?.state || 'excluded';
                    const extraCats = row.item.costable_category_ids.filter((c) => c !== row.item.primaryCat);
                    return (
                      <tr key={row.item.id}>
                        <td>
                          <span className="pm-benefit-name">
                            {row.item.name}
                            {row.coverage?.adjudication_note ? (
                              <Tooltip title={row.coverage.adjudication_note}>
                                {' '}<InfoCircleOutlined className="pm-note-icon" />
                              </Tooltip>
                            ) : null}
                            <Tooltip title="Edit name, categories, check-in types (catalog-level)">
                              <Button size="small" type="text" icon={<EditOutlined />} onClick={() => setEditItem(row.item)} />
                            </Tooltip>
                          </span>
                          {extraCats.length ? (
                            <span className="pm-benefit-sub">
                              also in: {extraCats.map((c) => categoryName(c)).join(', ')}
                            </span>
                          ) : null}
                        </td>
                        <td>
                          <Segmented
                            size="small"
                            className={`pm-seg-${state}`}
                            value={state}
                            onChange={(v) => setState(row, v)}
                            options={[
                              { label: 'Excluded', value: 'excluded' },
                              { label: 'Covered', value: 'uncapped' },
                              { label: 'Capped', value: 'capped' },
                            ]}
                          />
                        </td>
                        <td>
                          {state === 'capped' && row.pool ? (
                            <Space size={6} wrap>
                              <b style={{ fontVariantNumeric: 'tabular-nums' }}>{limitLabel(row.pool)}</b>
                              <span className="pm-chip pm-chip-muted">{windowChip(row.pool)}</span>
                              {Number(row.pool.subscriber_count) > 1 ? (
                                <Tooltip title={`Shared pool: ${row.pool.name}`}>
                                  <span className="pm-chip pm-chip-blue">shared</span>
                                </Tooltip>
                              ) : null}
                              <Button size="small" type="text" icon={<EditOutlined />} onClick={() => setCapRow(row)} />
                            </Space>
                          ) : state === 'uncapped' ? (
                            <span className="pm-benefit-sub">rides envelope</span>
                          ) : (
                            <span className="pm-benefit-sub">not covered (documented)</span>
                          )}
                        </td>
                        <td>
                          <Space size={4} wrap>
                            {row.item.checkin_type_ids.map((id) => (
                              <span key={id} className="pm-chip pm-chip-accent">{typeName(id)}</span>
                            ))}
                          </Space>
                        </td>
                        <td>
                          <Space size={0}>
                            <Tooltip title="Adjudication note">
                              <Button
                                size="small" type="text" icon={<InfoCircleOutlined />}
                                onClick={() => { setNoteRow(row); setNoteDraft(row.coverage?.adjudication_note || ''); }}
                              />
                            </Tooltip>
                            <Popconfirm title="Remove from this plan (back to excluded-by-default)?" onConfirm={() => clearRow(row)}>
                              <Button size="small" type="text" danger>×</Button>
                            </Popconfirm>
                          </Space>
                        </td>
                      </tr>
                    );
                  })}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
        )}
        <div className="pm-benefit-sub" style={{ marginTop: 6 }}>
          Benefits not on this list are excluded by default — a request against them
          verdicts “not covered” under enforcement.
        </div>
      </div>

      <div className="pm-rail" style={{ width: 290, flexShrink: 0 }}>
        <SetupSection planId={planId} plan={plan} onChanged={() => { load(); onDesignChanged?.(); }} />
        <hr />
        <h5>Cardinality — mapping load</h5>
        <div className="pm-rail-row"><span>Auto-resolving pairs</span><b>{card.auto}</b></div>
        <div className="pm-rail-row"><span>Mapping required</span><b>{card.ambiguous}</b></div>
        <div className="pm-rail-row"><span>Not covered</span><b>{card.not_covered}</b></div>
        {(card.pairs || []).filter((p) => p.resolution === 'mapping_required').slice(0, 6).map((p) => (
          <div key={`${p.checkin_type_id}:${p.costable_category_id}`} className="pm-rail-row" style={{ fontSize: 11.5 }}>
            <span>{typeName(p.checkin_type_id)} · {categoryName(p.costable_category_id)}</span>
            <b>{p.candidates.length} → map</b>
          </div>
        ))}
        {(() => {
          const notCovered = (card.pairs || []).filter(
            (p) => p.resolution === 'not_covered' && !dismissedWarnings[`${p.checkin_type_id}:${p.costable_category_id}`]
          );
          const dismissedCount = card.not_covered - notCovered.length;
          if (!notCovered.length && !dismissedCount) return null;
          return (
            <>
              <hr />
              {notCovered.length ? (
                <div className="pm-rail-warn">
                  ⚠ {notCovered.length} pair(s) have 0 covered items — requests there will verdict “not covered”. Dismiss each if intentional.
                </div>
              ) : null}
              {notCovered.map((p) => {
                const key = `${p.checkin_type_id}:${p.costable_category_id}`;
                return (
                  <div key={`nc-${key}`} className="pm-rail-row" style={{ fontSize: 11.5 }}>
                    <span>{typeName(p.checkin_type_id)} · {categoryName(p.costable_category_id)}</span>
                    <Button
                      size="small" type="link" style={{ fontSize: 11, padding: 0, height: 'auto' }}
                      onClick={() => setDismissedWarnings((d) => ({ ...d, [key]: true }))}
                    >
                      dismiss
                    </Button>
                  </div>
                );
              })}
              {dismissedCount > 0 ? (
                <div className="pm-rail-row" style={{ fontSize: 11 }}>
                  <span style={{ color: 'var(--pm-ink-faint)' }}>{dismissedCount} warning(s) dismissed — still not covered</span>
                  <Button size="small" type="link" style={{ fontSize: 11, padding: 0, height: 'auto' }} onClick={() => setDismissedWarnings({})}>
                    show
                  </Button>
                </div>
              ) : null}
            </>
          );
        })()}
      </div>

      <Modal
        open={detailsOpen}
        title="Plan name & segment"
        onCancel={() => setDetailsOpen(false)}
        onOk={saveDetails}
        okText="Save"
        confirmLoading={detailsSaving}
        destroyOnClose
      >
        <Space direction="vertical" style={{ width: '100%' }} size={10}>
          <Input value={detailsName} onChange={(e) => setDetailsName(e.target.value)} placeholder="Plan name" />
          {design?.profile?.plan_type === 'utility' ? (
            <span className="pm-benefit-sub">Utility plans have no retail/corporate/SME segment.</span>
          ) : (
            <Select
              mode="multiple"
              style={{ width: '100%' }}
              placeholder="Segments"
              value={detailsSegments}
              onChange={setDetailsSegments}
              options={[
                { value: 'retail', label: 'Retail' },
                { value: 'corporate', label: 'Corporate' },
                { value: 'sme', label: 'SME' },
              ]}
            />
          )}
        </Space>
      </Modal>

      <CapEditorModal
        open={!!capRow}
        onClose={() => setCapRow(null)}
        planId={planId}
        row={capRow}
        pools={design?.pools}
        onSaved={() => { load(); onDesignChanged?.(); }}
      />

      <ItemEditorModal
        open={!!editItem}
        onClose={() => setEditItem(null)}
        meta={meta}
        item={editItem}
        onSaved={() => { load(); onDesignChanged?.(); }}
      />

      <Modal
        open={!!noteRow}
        title={<span>Adjudication note — {noteRow?.item?.name}</span>}
        onCancel={() => setNoteRow(null)}
        onOk={saveNote}
        okText="Save note"
        destroyOnClose
      >
        <Input.TextArea
          value={noteDraft}
          onChange={(e) => setNoteDraft(e.target.value)}
          autoSize={{ minRows: 3, maxRows: 6 }}
          placeholder="Human-judged condition (e.g. SCBU/NICU cases only). Surfaced to CO at PA — never machine-evaluated."
        />
      </Modal>

      <Modal
        open={addOpen}
        title="Add benefit to this plan"
        onCancel={() => setAddOpen(false)}
        onOk={addExisting}
        okText="Add to plan"
        okButtonProps={{ disabled: !addPick }}
        destroyOnClose
      >
        <Space direction="vertical" style={{ width: '100%' }} size={10}>
          <Select
            showSearch
            style={{ width: '100%' }}
            placeholder="Pick from the catalog…"
            value={addPick}
            onChange={setAddPick}
            optionFilterProp="label"
            options={(catalog || [])
              .filter((i) => Number(i.status) === 1 && !onPlanItemIds.has(Number(i.id)))
              .map((i) => ({ value: i.id, label: `${i.name} (${categoryName(i.costable_category_id)})` }))}
            notFoundContent={
              (catalog || []).filter((i) => Number(i.status) === 1).length === 0
                ? 'No active Benefit Items yet — create one on the Benefit Item Mapping tab'
                : 'Everything in the catalog is already on this plan'
            }
          />
          {addPick ? (
            <>
              <Segmented
                value={addState}
                onChange={setAddState}
                options={[
                  { label: 'Excluded', value: 'excluded' },
                  { label: 'Covered', value: 'uncapped' },
                  { label: 'Capped', value: 'capped' },
                ]}
              />
              {addState === 'capped' ? (
                <>
                  <Segmented
                    value={addPoolMode}
                    onChange={setAddPoolMode}
                    options={[
                      { label: 'Own pool', value: 'own' },
                      { label: 'Shared pool', value: 'shared' },
                    ]}
                  />
                  {addPoolMode === 'own' ? (
                    <Space wrap>
                      <Select value={addMetric} onChange={setAddMetric} style={{ width: 160 }} options={METRIC_OPTIONS} />
                      {addMetric === 'text' ? (
                        <Input
                          value={addLimitText}
                          onChange={(e) => setAddLimitText(e.target.value)}
                          style={{ width: 220 }}
                          placeholder="e.g. BCG only"
                        />
                      ) : (
                        <InputNumber
                          value={addLimit}
                          onChange={setAddLimit}
                          min={1}
                          style={{ width: 160 }}
                          placeholder="Limit"
                          formatter={(v) => (addMetric === 'money' ? `${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',') : `${v}`)}
                          parser={(v) => `${v}`.replace(/,/g, '')}
                        />
                      )}
                      <Select
                        value={addWindow}
                        onChange={setAddWindow}
                        style={{ width: 160 }}
                        options={[
                          { value: 'policy_year', label: 'Policy year' },
                          { value: 'calendar_month', label: 'Calendar month' },
                          { value: 'rolling_months', label: 'Rolling months' },
                        ]}
                      />
                      {addWindow === 'rolling_months' ? (
                        <InputNumber value={addMonths} onChange={setAddMonths} min={1} max={120} addonAfter="mo" style={{ width: 110 }} />
                      ) : null}
                    </Space>
                  ) : (
                    <Select
                      value={addSharedPool}
                      onChange={setAddSharedPool}
                      style={{ width: '100%' }}
                      placeholder="Subscribe to an existing pool (e.g. Accommodation)"
                      options={addSharablePools.map((p) => ({ value: p.id, label: `${p.name} — ${limitLabel(p)} · ${windowChip(p)}` }))}
                      notFoundContent="No other benefit pools on this plan yet"
                    />
                  )}
                </>
              ) : null}
              <Input.TextArea
                value={addNote}
                onChange={(e) => setAddNote(e.target.value)}
                placeholder="Adjudication note (optional — surfaced to CO at PA, never machine-evaluated)"
                autoSize={{ minRows: 2, maxRows: 4 }}
              />
            </>
          ) : null}
          <span className="pm-benefit-sub" style={{ display: 'block' }}>
            Benefit Items are global. A new one is created on the Benefit Item Mapping tab (name, request category, applicable check-in types) and then selected here, so the same item is reused across plans instead of duplicated.
          </span>
        </Space>
      </Modal>

      <Modal
        open={copyOpen}
        title="Copy design from another plan"
        onCancel={() => setCopyOpen(false)}
        onOk={() => runCopy(false)}
        okText="Copy"
        okButtonProps={{ disabled: !copySource }}
        destroyOnClose
      >
        <Space direction="vertical" style={{ width: '100%' }} size={10}>
          <Select
            placeholder="Source plan"
            style={{ width: '100%' }}
            value={copySource}
            onChange={setCopySource}
            showSearch
            optionFilterProp="label"
            options={(plans || [])
              .filter((p) => Number(p.plan_id) !== Number(planId))
              .map((p) => ({ value: p.plan_id, label: `${p.plan_name}${p.coverage_rows > 0 ? ` (${p.coverage_rows} rows)` : ' (no design)'}` }))}
          />
          <span className="pm-benefit-sub">
            One-time, independent copy of the benefit list — <b>without limits</b>: set each
            cap on this plan yourself. Envelopes, bands and prices are not copied. The target
            must be empty (you&apos;ll be asked to clear it otherwise).
          </span>
        </Space>
      </Modal>
    </div>
  );
}
